import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form/input';
import { Brain, User, Send, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { OnboardingOptionChips } from './OnboardingOptionChips';
import { DevicesConnectCard } from './DevicesConnectCard';
import { SystemProcessingIndicator } from '@/components/coach/SystemProcessingIndicator';
import { quickCompleteOnboarding } from '@/lib/api';
import { saveProfile, saveOnboardingAdditionalData, saveOnboardingPlans, saveSeasonPlan } from '@/lib/storage';
import { toast } from '@/hooks/use-toast';
import type { AthleteProfile, Sport } from '@/types';
import { useAuth } from '@/context/AuthContext';
import { detectTimezone } from '@/lib/timezone';
import {
  trackOnboardingStepViewed,
  trackOnboardingStepCompleted,
  trackOnboardingPlanGenerating,
  trackOnboardingPlanReady,
  trackOnboardingCompletedQuick,
} from '@/lib/posthog';
import { log } from '@/lib/logger';
import { hasAIDataConsent, setAIDataConsent } from '@/components/coach/AIDataConsentDialog';
import { Checkbox } from '@/components/ui/form/checkbox';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AthleteOnboardingProfile {
  sports: Sport[];
  consistency: string;
  goal: string;
  raceDetails: string;
  raceGoalTime: string;
  availableDays: number;
  hoursPerWeek: number;
  hasInjury: boolean;
  injuryDetails: string;
  stravaConnected: boolean;
  targetEvent?: {
    name?: string;
    date?: string;
  };
}

interface OnboardingChatProps {
  onComplete: (profile: AthleteOnboardingProfile) => Promise<void>;
  isComplete: boolean;
}

type Step =
  | 'sport'        // Step 1: What sport?
  | 'goal'         // Step 2: What goal?
  | 'race-details' // Step 2b: Race details (if goal=race)
  | 'days'         // Step 3: How many days?
  | 'generating'   // Plan generation in progress
  | 'device'       // Step 4: Device connection
  | 'complete';    // Done

interface Message {
  id: string;
  role: 'coach' | 'athlete';
  content: string;
  component?: 'sport' | 'goal' | 'days' | 'device' | 'text-input';
}

const STEP_MAP: Record<string, number> = { sport: 1, goal: 2, days: 3, device: 4 };
const TOTAL_STEPS = 4;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardingChat({ onComplete, isComplete }: OnboardingChatProps) {
  const { status, user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('sport');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onboardingStartTime = useRef(Date.now());
  const stepStartTime = useRef(Date.now());

  const [aiConsentChecked, setAiConsentChecked] = useState(() => hasAIDataConsent());

  // Collected data — minimal for v2
  const [data, setData] = useState({
    primarySport: '' as 'run' | 'bike' | 'tri' | '',
    goalType: '' as 'performance' | 'completion' | 'general' | '',
    availableDays: 0,
    raceName: '',
    raceDate: '',
    deviceConnected: false,
  });

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

  // ---------------------------------------------------------------------------
  // Message helpers
  // ---------------------------------------------------------------------------

  const addCoachMessage = useCallback((content: string, component?: Message['component'], onDone?: () => void) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `c-${Date.now()}-${Math.random()}`,
        role: 'coach',
        content,
        component,
      }]);
      setIsTyping(false);
      onDone?.();
    }, 500 + Math.random() * 300);
  }, []);

  const addAthleteMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, {
      id: `a-${Date.now()}-${Math.random()}`,
      role: 'athlete',
      content,
    }]);
  }, []);

  // ---------------------------------------------------------------------------
  // Analytics helpers
  // ---------------------------------------------------------------------------

  const trackStepView = (stepName: string) => {
    const num = STEP_MAP[stepName] || 0;
    trackOnboardingStepViewed(stepName, num);
    stepStartTime.current = Date.now();
  };

  const trackStepDone = (stepName: string, value: string) => {
    const num = STEP_MAP[stepName] || 0;
    const elapsed = Date.now() - stepStartTime.current;
    trackOnboardingStepCompleted(stepName, num, value, elapsed);
  };

  // ---------------------------------------------------------------------------
  // Step 0: Welcome — start with sport question
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (messages.length === 0) {
      const firstName = user?.first_name;
      const greeting = firstName
        ? t('onboarding.v2.welcomeName', { name: firstName, defaultValue: `Hey ${firstName}! I'm your AI training coach. Let's get you set up in under a minute.` })
        : t('onboarding.v2.welcome', { defaultValue: "Welcome! I'm your AI training coach. Let's get you set up in under a minute." });

      setTimeout(() => {
        addCoachMessage(greeting, undefined, () => {
          setTimeout(() => {
            addCoachMessage(
              t('onboarding.v2.askSport', { defaultValue: "What sport are you training for?" }),
              'sport'
            );
            trackStepView('sport');
          }, 400);
        });
      }, 400);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Step 1: Sport selection
  // ---------------------------------------------------------------------------

  const handleSportSelect = (selected: string[]) => {
    const sport = selected[0];
    const sportMap: Record<string, 'run' | 'bike' | 'tri'> = {
      running: 'run', cycling: 'bike', triathlon: 'tri',
    };
    const primarySport = sportMap[sport] || 'run';
    setData(prev => ({ ...prev, primarySport }));

    const displayName = sport.charAt(0).toUpperCase() + sport.slice(1);
    addAthleteMessage(displayName);
    trackStepDone('sport', sport);

    setTimeout(() => {
      addCoachMessage(
        t('onboarding.v2.askGoal', { sport: displayName, defaultValue: `Great choice! What's driving your ${displayName.toLowerCase()} training right now?` }),
        'goal'
      );
      setStep('goal');
      trackStepView('goal');
    }, 300);
  };

  // ---------------------------------------------------------------------------
  // Step 2: Goal selection
  // ---------------------------------------------------------------------------

  const handleGoalSelect = (selected: string[]) => {
    const goalId = selected[0];
    const goalMap: Record<string, 'performance' | 'completion' | 'general'> = {
      race: 'completion', performance: 'performance', returning: 'general', maintaining: 'general',
    };
    setData(prev => ({ ...prev, goalType: goalMap[goalId] || 'general' }));

    const labels: Record<string, string> = {
      race: t('onboarding.goals.race', { defaultValue: 'Training for a race' }),
      performance: t('onboarding.goals.performance', { defaultValue: 'Get faster / stronger' }),
      returning: t('onboarding.goals.returning', { defaultValue: 'Getting back into it' }),
      maintaining: t('onboarding.goals.maintaining', { defaultValue: 'Staying fit' }),
    };
    addAthleteMessage(labels[goalId] || goalId);
    trackStepDone('goal', goalId);

    if (goalId === 'race') {
      setTimeout(() => {
        addCoachMessage(
          t('onboarding.v2.askRace', { defaultValue: "Nice! What race, and when is it? (You can skip this for now)" }),
          'text-input'
        );
        setStep('race-details');
        setShowTextInput(true);
      }, 300);
    } else {
      proceedToDays();
    }
  };

  const handleRaceDetails = () => {
    const input = textInput.trim();
    if (input) {
      const dateMatch = input.match(/\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}/);
      const eventName = input.split(/[,\n]/)[0].trim();
      setData(prev => ({
        ...prev,
        raceName: eventName || '',
        raceDate: dateMatch ? dateMatch[0] : '',
      }));
      addAthleteMessage(input);
    }
    setTextInput('');
    setShowTextInput(false);
    proceedToDays();
  };

  const handleRaceSkip = () => {
    setTextInput('');
    setShowTextInput(false);
    addAthleteMessage(t('onboarding.v2.skipRace', { defaultValue: "I'll decide later" }));
    proceedToDays();
  };

  const proceedToDays = () => {
    setTimeout(() => {
      addCoachMessage(
        t('onboarding.v2.askDays', { defaultValue: "How many days a week can you train?" }),
        'days'
      );
      setStep('days');
      trackStepView('days');
    }, 300);
  };

  // ---------------------------------------------------------------------------
  // Step 3: Days per week → trigger plan generation
  // ---------------------------------------------------------------------------

  const proceedToGenerate = (days: number) => {
    setStep('generating');
    const sportDisplay = data.primarySport === 'run' ? 'running' : data.primarySport === 'bike' ? 'cycling' : 'triathlon';
    trackOnboardingPlanGenerating({ sport: sportDisplay, goal: data.goalType, days });

    addCoachMessage(
      t('onboarding.v2.generating', { defaultValue: "Analyzing your profile and setting up your coaching guidance..." }),
      undefined,
      () => generatePlan(days)
    );
  };

  const handleDaysSelect = (selected: string[]) => {
    const days = Number(selected[0]);
    setData(prev => ({ ...prev, availableDays: days }));
    addAthleteMessage(t('onboarding.options.daysPerWeek', { n: days, defaultValue: `${days} days per week` }));
    trackStepDone('days', String(days));

    proceedToGenerate(days);
  };

  const generatePlan = async (days: number) => {
    const genStart = Date.now();

    if (status !== "authenticated") {
      addCoachMessage(t('onboarding.coach.authRequired', { defaultValue: "Please sign in first to continue." }));
      return;
    }

    try {
      const result = await quickCompleteOnboarding({
        primary_sport: data.primarySport as 'run' | 'bike' | 'tri',
        goal_type: data.goalType as 'performance' | 'completion' | 'general',
        availability_days_per_week: days,
        role: 'athlete',
        first_name: user?.first_name || null,
        last_name: user?.last_name || null,
        timezone: detectTimezone() || null,
        race_name: data.raceName || null,
        race_date: data.raceDate || null,
      });

      if (result.status !== 'ok') {
        throw new Error('Backend returned non-ok status');
      }

      // Save plans locally
      saveOnboardingPlans({
        weekly_intent: result.weekly_intent,
        season_plan: result.season_plan,
        provisional: result.provisional,
        warning: result.warning,
        savedAt: new Date().toISOString(),
      });
      if (result.season_plan) {
        saveSeasonPlan(result.season_plan);
      }

      const genDuration = Date.now() - genStart;
      trackOnboardingPlanReady({
        duration_ms: genDuration,
        provisional: result.provisional,
        sessions_count: result.sessions_count,
      });

      // Show coaching guidance summary + coach/plan intro + device connection
      const intent = result.weekly_intent as { focus?: string; volume_target_hours?: number; intensity_distribution?: string } | undefined;
      const guidanceMsg = intent
        ? t('onboarding.v2.guidanceReady', {
            focus: intent.focus,
            volume: intent.volume_target_hours,
            intensity: intent.intensity_distribution,
            defaultValue: `Here's your weekly coaching guidance:\n\nFocus: ${intent.focus}\nVolume target: ${intent.volume_target_hours}h\nIntensity: ${intent.intensity_distribution}`,
          })
        : t('onboarding.v2.guidanceReadyGeneric', { defaultValue: "Your coaching profile is set up and ready." });

      addCoachMessage(guidanceMsg, undefined, () => {
        setTimeout(() => {
          addCoachMessage(
            t('onboarding.v2.coachIntro', {
              defaultValue: "You can chat with me anytime to ask questions, get advice, or build a full training plan. Head to the Plan tab when you're ready to create your first structured plan.",
            }),
            undefined,
            () => {
              setTimeout(() => {
                addCoachMessage(
                  t('onboarding.v2.connectDevice', {
                    defaultValue: "First though — connect your watch so I can import your training history and give you smarter recommendations.",
                  }),
                  'device'
                );
                setStep('device');
                trackStepView('device');
              }, 500);
            }
          );
        }, 600);
      });
    } catch (error) {
      log.error('ONBOARDING', 'Quick onboarding failed:', error);
      let errorMessage = t('onboarding.errors.generic', { defaultValue: 'Something went wrong' });
      if (error && typeof error === 'object') {
        const apiError = error as { message?: string; response?: { data?: { detail?: string } } };
        if (apiError.response?.data?.detail) errorMessage = apiError.response.data.detail;
        else if (apiError.message) errorMessage = apiError.message;
      }
      addCoachMessage(t('onboarding.coach.errorMessage', { error: errorMessage, defaultValue: `Oops: ${errorMessage}` }));
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    }
  };

  // ---------------------------------------------------------------------------
  // Step 4: Device connection (after plan preview)
  // ---------------------------------------------------------------------------

  const handleDeviceConnect = (provider: 'strava' | 'garmin') => {
    setData(prev => ({ ...prev, deviceConnected: true }));
    addAthleteMessage(
      provider === 'strava'
        ? t('onboarding.athlete.connectedStrava', { defaultValue: 'Connected Strava' })
        : t('onboarding.athlete.connectedGarmin', { defaultValue: 'Connected Garmin' })
    );

    setTimeout(() => {
      addCoachMessage(
        t('onboarding.v2.importing', {
          defaultValue: "Importing your training history... I'll use this to give you better recommendations.",
        }),
        undefined,
        () => finishOnboarding(true)
      );
    }, 300);
  };

  const handleDevicesSkip = () => {
    addAthleteMessage(t('onboarding.v2.skipDevice', { defaultValue: "Skip for now" }));
    setTimeout(() => {
      addCoachMessage(
        t('onboarding.v2.noDevice', {
          defaultValue: "No problem! You can connect anytime in Settings. Let's get started.",
        }),
        undefined,
        () => finishOnboarding(false)
      );
    }, 300);
  };

  // ---------------------------------------------------------------------------
  // Finish — save profile + redirect
  // ---------------------------------------------------------------------------

  const finishOnboarding = async (deviceConnected: boolean) => {
    const totalTime = Date.now() - onboardingStartTime.current;
    const sportDisplay = data.primarySport === 'run' ? 'running' : data.primarySport === 'bike' ? 'cycling' : 'triathlon';

    trackOnboardingCompletedQuick({
      total_time_ms: totalTime,
      sport: sportDisplay,
      goal: data.goalType,
      days: data.availableDays,
    });

    // Build profile for local storage + parent callback
    const profile: AthleteOnboardingProfile = {
      sports: [sportDisplay as Sport],
      consistency: 'occasional',
      goal: data.goalType,
      raceDetails: data.raceName,
      raceGoalTime: '',
      availableDays: data.availableDays,
      hoursPerWeek: Math.round(data.availableDays * 1.5),
      hasInjury: false,
      injuryDetails: '',
      stravaConnected: deviceConnected,
    };

    const localProfile: AthleteProfile = {
      id: 'local',
      name: user?.first_name || '',
      sports: profile.sports,
      trainingAge: 1,
      weeklyAvailability: {
        daysPerWeek: data.availableDays,
        hoursPerWeek: profile.hoursPerWeek,
      },
      goals: data.goalType ? [data.goalType] : [],
      stravaConnected: deviceConnected,
      onboardingComplete: false,
    };
    saveProfile(localProfile);
    saveOnboardingAdditionalData({
      consistency: 'occasional',
      raceDetails: data.raceName,
      injuryDetails: '',
      collectedAt: new Date().toISOString(),
    });

    setStep('complete');

    setTimeout(async () => {
      try {
        await onComplete(profile);
      } catch (error) {
        log.error('ONBOARDING', 'Error in onComplete callback:', error);
        toast({
          title: t('onboarding.toasts.errorTitle', { defaultValue: 'Error' }),
          description: t('onboarding.toasts.completionError', { defaultValue: 'Failed to complete. Please refresh.' }),
          variant: 'destructive',
        });
      }
    }, 800);
  };

  // ---------------------------------------------------------------------------
  // Progress bar
  // ---------------------------------------------------------------------------

  const currentStepNum = STEP_MAP[step] || (step === 'generating' ? 3 : null);

  return (
    <div className="flex-1 flex flex-col px-3 py-3 md:p-6 min-h-0 overflow-hidden">
      {/* System Status header */}
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#00FFC8]/70 mb-4 flex items-center gap-4">
        <span>ONBOARDING: {isComplete ? 'COMPLETE' : step === 'generating' ? 'GENERATING' : 'ACTIVE'}</span>
        <span className="text-white/30">|</span>
        <span className="text-white/50">QUICK START v2</span>
      </div>

      {/* Segmented Power Bar */}
      {currentStepNum && !isComplete && step !== 'complete' && (
        <div className="shrink-0 mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
              {step === 'generating' ? 'SETTING UP' : 'CALIBRATION'}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#00FFC8]/80">
              {currentStepNum}/{TOTAL_STEPS}
            </span>
          </div>
          <div className="flex gap-1" role="progressbar" aria-valuenow={currentStepNum} aria-valuemin={0} aria-valuemax={TOTAL_STEPS}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
              const filled = i < currentStepNum;
              const isLatest = i === currentStepNum - 1;
              const isPulsing = isLatest && step === 'generating';
              return (
                <div
                  key={i}
                  className={cn(
                    'h-[3px] flex-1 rounded-full transition-all duration-500',
                    filled
                      ? 'bg-[#00FFC8] shadow-[0_0_12px_rgba(0,255,200,0.5)]'
                      : 'bg-white/[0.08]',
                    isLatest && !isPulsing && 'animate-pulse shadow-[0_0_18px_rgba(0,255,200,0.6)]',
                    isPulsing && 'animate-[pulse_0.8s_ease-in-out_infinite] shadow-[0_0_24px_rgba(0,255,200,0.8)]'
                  )}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4">
        {messages.map((message) => (
          <div key={message.id} className="animate-fade-up">
            <MessageBubble message={message} />

            {/* Step 1: Sport selection */}
            {message.component === 'sport' && step === 'sport' && (
              <OnboardingOptionChips
                options={[
                  { id: 'running', label: t('onboarding.sports.running', { defaultValue: 'Running' }) },
                  { id: 'cycling', label: t('onboarding.sports.cycling', { defaultValue: 'Cycling' }) },
                  { id: 'triathlon', label: t('onboarding.sports.triathlon', { defaultValue: 'Triathlon' }) },
                ]}
                onSelect={handleSportSelect}
              />
            )}

            {/* Step 2: Goal selection */}
            {message.component === 'goal' && step === 'goal' && (
              <OnboardingOptionChips
                options={[
                  { id: 'race', label: t('onboarding.goals.race', { defaultValue: 'Training for a race' }) },
                  { id: 'performance', label: t('onboarding.goals.performance', { defaultValue: 'Get faster / stronger' }) },
                  { id: 'returning', label: t('onboarding.goals.returning', { defaultValue: 'Getting back into it' }) },
                  { id: 'maintaining', label: t('onboarding.goals.maintaining', { defaultValue: 'Staying fit' }) },
                ]}
                onSelect={handleGoalSelect}
              />
            )}

            {/* Step 3: Days per week + AI consent checkbox */}
            {message.component === 'days' && step === 'days' && (
              <>
                <OnboardingOptionChips
                  options={[3, 4, 5, 6, 7].map(n => ({
                    id: String(n),
                    label: t('onboarding.options.days', { n, defaultValue: `${n} days` }),
                  }))}
                  onSelect={handleDaysSelect}
                  disabled={!aiConsentChecked}
                />

                {/* AI data consent — inline checkbox */}
                <div className="ml-6 sm:ml-12 mt-3 rounded-lg border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm px-4 py-3 space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={aiConsentChecked}
                      onCheckedChange={(checked) => {
                        const value = checked === true;
                        setAiConsentChecked(value);
                        setAIDataConsent(value);
                      }}
                      className="mt-0.5 border-[#00FFC8]/40 data-[state=checked]:bg-[#00FFC8] data-[state=checked]:text-black"
                    />
                    <span className="text-sm text-foreground leading-snug">
                      I agree to allow AthleteSpace to use AI services (OpenAI) to generate coaching insights.
                    </span>
                  </label>
                  <p className="text-xs text-muted-foreground leading-relaxed pl-7">
                    Your messages and training metrics may be securely processed by our AI provider to generate responses.{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#00FFC8]/80 hover:text-[#00FFC8] underline">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </>
            )}

            {/* Step 4: Device connection */}
            {message.component === 'device' && step === 'device' && (
              <DevicesConnectCard
                onConnect={handleDeviceConnect}
                onSkip={handleDevicesSkip}
              />
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="animate-fade-up">
            <SystemProcessingIndicator className="ml-0" />
          </div>
        )}

        {/* Generating animation */}
        {step === 'generating' && !isTyping && (
          <div className="flex items-center gap-3 ml-6 sm:ml-12 mt-2 animate-fade-up">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-[#00FFC8] animate-[bounce_0.6s_ease-in-out_infinite]" />
              <div className="w-2 h-2 rounded-full bg-[#00FFC8] animate-[bounce_0.6s_ease-in-out_0.15s_infinite]" />
              <div className="w-2 h-2 rounded-full bg-[#00FFC8] animate-[bounce_0.6s_ease-in-out_0.3s_infinite]" />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#00FFC8]/60">
              Setting up coaching guidance...
            </span>
          </div>
        )}

        {/* Completion state */}
        {isComplete && (
          <div className="flex flex-col items-center justify-center py-8 animate-fade-up">
            <CheckCircle2 className="h-12 w-12 text-load-fresh mb-3" />
            <p className="text-foreground font-medium">{t('onboarding.ui.settingUp', { defaultValue: 'Setting up your dashboard...' })}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Text input — for race details */}
      {showTextInput && !isComplete && (
        <div className="shrink-0 flex flex-col gap-2 pt-2 pb-[calc(0.25rem+env(safe-area-inset-bottom,0px))] border-t border-white/[0.08]">
          <div className="flex gap-2">
            <Input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && step === 'race-details') handleRaceDetails();
              }}
              placeholder={t('onboarding.v2.racePlaceholder', { defaultValue: 'e.g. Boston Marathon, 2026-04-20' })}
              className={cn(
                'flex-1 font-mono text-base sm:text-xs bg-white/[0.03] border-white/[0.1]',
                'focus:border-[#00FFC8]/30 focus:ring-[#00FFC8]/20 placeholder:text-white/30'
              )}
            />
            <Button
              onClick={handleRaceDetails}
              disabled={!textInput.trim()}
              className={cn(
                'font-mono text-[10px] uppercase tracking-[0.12em] border rounded-md',
                'border-[rgba(0,255,200,0.4)] bg-[rgba(0,255,200,0.1)] text-[#00FFC8]',
                'hover:bg-[rgba(0,255,200,0.18)] hover:shadow-[0_0_20px_rgba(0,255,200,0.25)]',
                'active:scale-95 transition-all duration-150'
              )}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          {step === 'race-details' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRaceSkip}
              className="self-start text-muted-foreground text-xs"
            >
              {t('onboarding.v2.skipRace', { defaultValue: "I'll decide later" })}
            </Button>
          )}
        </div>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Message Bubble (same HUD glass style)
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { message: Message }) {
  const isCoach = message.role === 'coach';

  return (
    <div className={cn('flex gap-2 sm:gap-3', !isCoach && 'flex-row-reverse')}>
      <div className={cn(
        'w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 border',
        isCoach ? 'bg-white/[0.06] border-[#00FFC8]/30 text-[#00FFC8]/90' : 'bg-white/[0.04] border-[#3B82F6]/40 text-[#93C5FD]'
      )}>
        {isCoach ? (
          <Brain className="h-4 w-4" />
        ) : (
          <User className="h-4 w-4" />
        )}
      </div>
      <div
        className={cn(
          'max-w-[85%] sm:max-w-[80%] rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 font-mono text-xs leading-relaxed',
          isCoach
            ? 'rounded-tl-sm border-l-2 border-[#00FFC8]/80 bg-[#1a1f2e]/90 backdrop-blur-[12px] text-foreground'
            : 'rounded-tr-sm border-r-2 border-[#3B82F6]/80 bg-[#0f172a]/95 text-slate-200'
        )}
      >
        <p className="text-sm leading-relaxed">{message.content}</p>
      </div>
    </div>
  );
}
